import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import './TeacherFreeTier.css';

/**
 * NotebookPanel — teacher dashboard section for the Gemini Notebook
 * (formerly NotebookLM) outcome loop.
 *
 * Friction-first design: the primary flow is ONE action — paste your
 * materials (URLs, YouTube links, summary text, any mix) and/or drop
 * PDFs → Import → walk away with a shareable student link. Assignments
 * auto-link the teacher's class code, so the link is self-sufficient
 * for brand-new students.
 */

const BOOKMARKLET_HREF =
  "javascript:(function(){var t=(document.title||'').replace(/\\s*[-\\u2013|]\\s*(NotebookLM|Gemini Notebook).*$/i,'').trim()||'Gemini Notebook import';" +
  "var l=Array.prototype.map.call(document.querySelectorAll('a[href]'),function(a){return a.href})" +
  ".filter(function(h){return /youtube\\.com\\/watch|youtu\\.be\\//.test(h)});" +
  "l=l.filter(function(h,i){return l.indexOf(h)===i}).slice(0,10);" +
  "var s='';try{s=String(window.getSelection())}catch(e){}" +
  "var p={title:t,summary:s.slice(0,60000),youtube:l};" +
  "window.open('https://app.teachtappy.ai/notebook-import#'+btoa(unescape(encodeURIComponent(JSON.stringify(p)))),'_blank');})();";

const YT_RE = /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/i;

// Google Classroom direct share URL (no third-party script — the official
// widget alternative per developers.google.com/workspace/classroom/guides/sharebutton).
// itemtype=assignment drops the teacher straight into Classroom's
// assignment-creation dialog with our link attached.
const openClassroomShare = (url, title) => {
  const share =
    'https://classroom.google.com/share?url=' + encodeURIComponent(url) +
    '&itemtype=assignment&title=' + encodeURIComponent(title || 'Tappy assignment');
  window.open(share, '_blank', 'noopener,width=680,height=600');
};

// Split pasted content into typed sources: each URL line becomes a
// url/youtube source; everything else pools into one text source.
// notebook.google.com is the post-rename (July 2026) domain.
const GEMINI_NB_RE = /notebooklm\.google\.com|notebook\.google\.com|gemini\.google\.com/i;

const parsePasted = (raw) => {
  const sources = [];
  const textLines = [];
  let geminiLinks = 0;
  raw.split('\n').forEach((line) => {
    const t = line.trim();
    if (/^https?:\/\/\S+$/.test(t)) {
      if (GEMINI_NB_RE.test(t)) {
        // Private to the teacher's Google account — a server fetch gets a
        // sign-in page that poisons retrieval. Field-reported by a teacher.
        geminiLinks += 1;
      } else {
        sources.push(YT_RE.test(t)
          ? { source_type: 'youtube', url: t }
          : { source_type: 'url', url: t, title: t.slice(0, 120) });
      }
    } else if (t) {
      textLines.push(line);
    }
  });
  const text = textLines.join('\n').trim();
  if (text.length > 40) {
    sources.push({ source_type: 'text', title: 'Pasted notes / summary', text });
  }
  return { sources, geminiLinks };
};

// Per-class share card: class name up front, Classroom share primary,
// copy + QR beside it. Assembled so the teacher never handles raw URLs.
const ShareCard = ({ link, defaultTitle }) => {
  const [copied, setCopied] = useState(false);
  const url = link.assignment_url;
  const shareTitle = defaultTitle || 'Tappy assignment';
  return (
    <div className="teacher-code-display teacher-code-inline"
         style={{ marginBottom: 10, flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      <QRCodeSVG value={url} size={64} fgColor="#1b3f48" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div className="teacher-code-label">{link.class_name || 'Your class'}</div>
        <div style={{ fontSize: 12, color: '#76909f', wordBreak: 'break-all' }}>{url}</div>
      </div>
      <button className="teacher-btn-primary"
              onClick={() => openClassroomShare(url, shareTitle)}>
        Add to Google Classroom
      </button>
      <button className="teacher-btn-secondary"
              onClick={async () => {
                try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
              }}>
        {copied ? '✓ Copied' : 'Copy link'}
      </button>
    </div>
  );
};

const NotebookPanel = () => {
  const [notebooks, setNotebooks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [outcomesFor, setOutcomesFor] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  // Smart importer state
  const [title, setTitle] = useState('');
  const [pasted, setPasted] = useState('');
  const [files, setFiles] = useState([]);
  const [newLinks, setNewLinks] = useState([]);
  // Instant grounded preview: Mira's opening line from the just-imported
  // sources — the aha moment (and garbage-import detector) at zero clicks.
  const [openingPreview, setOpeningPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  // Teacher's classes (multi-class: one per period) — assignment targeting.
  // With 2+ classes nothing is pre-selected: the teacher picks deliberately.
  // Pre-selecting all periods sent one subject's review to another
  // subject's periods for teachers who teach more than one course
  // (field-reported: AP Human review defaulting into AP Gov periods).
  const [classes, setClasses] = useState([]);
  const [targetClasses, setTargetClasses] = useState(null); // null until classes load
  const fileInput = useRef(null);

  // True while the only class was selected automatically, so that adding a
  // second period clears it instead of leaving period 1 silently selected.
  const autoSelected = useRef(false);

  const refresh = useCallback(() => {
    axios.get('/api/notebooks').then((r) => setNotebooks(r.data.notebooks || [])).catch(() => {});
    axios.get('/api/notebooks/assignments').then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
    axios.get('/api/classroom/my-class').then((r) => {
      const cs = r.data?.classes || [];
      setClasses(cs);
      setTargetClasses((prev) => {
        const ids = cs.map((c) => c.invite_id);
        if (ids.length === 1) {          // one class: nothing to choose
          autoSelected.current = true;
          return ids;
        }
        if (prev === null || autoSelected.current) {
          autoSelected.current = false;  // 2+ classes: start with none
          return [];
        }
        return prev.filter((i) => ids.includes(i)); // keep picks, drop deleted
      });
    }).catch(() => {});
  }, []);

  // Stay in sync when classes change elsewhere on the page (the dashboard
  // dispatches this after add/rename/delete) or the tab regains focus — a
  // stale class list silently mis-targeted assignments (field-reported).
  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener('tappy:classes-changed', onChange);
    window.addEventListener('focus', onChange);
    return () => {
      window.removeEventListener('tappy:classes-changed', onChange);
      window.removeEventListener('focus', onChange);
    };
  }, [refresh]);

  const toggleClass = (inviteId) => {
    setTargetClasses((prev) => {
      const cur = prev || [];
      return cur.includes(inviteId) ? cur.filter((i) => i !== inviteId) : [...cur, inviteId];
    });
  };

  // What actually gets sent: selected classes, or omit entirely when the
  // teacher has no class yet (server auto-creates one).
  const assignPayload = () => {
    const sel = targetClasses || [];
    return classes.length > 0 && sel.length > 0 ? { classroom_invite_ids: sel } : {};
  };

  useEffect(() => { refresh(); }, [refresh]);

  // Errors stay until the teacher's next action — a 5-second toast is too
  // short to read an explanation of WHY an import failed.
  const flash = (msg, sticky = false) => {
    setNotice(msg);
    if (!sticky) setTimeout(() => setNotice(''), 5000);
  };

  // Turn server failure reasons into one readable line. The server already
  // explains login walls and private links precisely; previously the panel
  // discarded that and showed only "1 failed".
  const describeFailures = (fails) => {
    if (!fails.length) return '';
    const first = fails[0].error || 'could not be read';
    const more = fails.length > 1 ? ` (${fails.length - 1} more had problems too.)` : '';
    return `${first}${more}`;
  };

  // The one-action flow: parse paste + upload PDFs + auto-assign → link.
  const importAndAssign = async () => {
    const { sources, geminiLinks } = parsePasted(pasted);
    if (geminiLinks > 0) {
      flash('Tappy can\u2019t open Gemini Notebook links, even public ones: Google shows a sign-in page instead of your notebook. Instead, open your notebook\u2019s Briefing Doc (or summary), select all, copy, and paste the text here. Or click the \ud83d\udcd4 Push to Tappy bookmarklet on that page.');
      if (sources.length === 0 && files.length === 0) return;
    }
    if (sources.length === 0 && files.length === 0) {
      flash('Paste your notebook\u2019s summary text, some links, or add a PDF first.');
      return;
    }
    if (classes.length > 1 && (targetClasses || []).length === 0) {
      flash('Pick at least one class to assign to.');
      return;
    }
    // Field-reported: unnamed assignments all default to "Class materials",
    // making the Assignments list unreadable. Two seconds of typing beats
    // an unreadable dashboard — the name is required.
    if (!title.trim()) {
      flash('Name this assignment first (e.g. "Unit 1 Review") — that’s how it appears in your list and to students.');
      return;
    }
    setBusy(true);
    setNewLinks([]);
    setOpeningPreview(null);
    setPreviewFailed(false);
    setNotice('');
    try {
      // 1. Create notebook + pasted sources (auto-assign off until PDFs land)
      const r = await axios.post('/api/notebooks/import', {
        title: title.trim() || 'Class materials',
        sources,
        auto_assign: false,
      });
      if (!r.data.success) throw new Error(r.data.error);
      const nbId = r.data.notebook_id;
      let ok = (r.data.ingested || []).length;
      const fails = [...(r.data.failures || [])];

      // 2. PDFs via multipart, sequentially
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        try {
          const fr = await axios.post(`/api/notebooks/${nbId}/sources`, form,
            { headers: { 'Content-Type': 'multipart/form-data' } });
          if (fr.data.success) ok += 1;
        } catch (err) {
          fails.push({ source: f.name, error: err.response?.data?.error || 'upload failed' });
        }
      }

      if (ok === 0) {
        // Remove the empty notebook so it doesn't sit in the list with
        // nothing in it or count against the notebook limit.
        try { await axios.delete(`/api/notebooks/${nbId}`); } catch (e) { /* best effort */ }
        flash(`Nothing was imported. ${describeFailures(fails)}`, true);
        refresh();
        return;
      }

      // 3. Assign → one shareable link per selected class
      const ar = await axios.post(`/api/notebooks/${nbId}/assignments`,
        { ...assignPayload(), title: title.trim() });
      if (ar.data.success) {
        setNewLinks(ar.data.assignments || [{ assignment_url: ar.data.assignment_url }]);
        fetchOpeningPreview(nbId);
        setTitle(''); setPasted(''); setFiles([]);
        // Next assignment may be a different subject — don't carry periods over.
        if (classes.length > 1) setTargetClasses([]);
        if (fileInput.current) fileInput.current.value = '';
        if (fails.length) {
          flash(`${ok} source${ok === 1 ? '' : 's'} imported. ${fails.length} couldn't be: ${describeFailures(fails)}`, true);
        } else {
          flash(`${ok} source${ok === 1 ? '' : 's'} imported. Link ready below.`);
        }
      }
      refresh();
    } catch (err) {
      flash(err.response?.data?.error || err.message || 'Import failed');
    } finally { setBusy(false); }
  };

  // Non-blocking: share cards render immediately; the preview streams in
  // when Mira's opener is ready.
  // A preview that fails must SAY so. It used to vanish silently — the one
  // check built to catch a bad import (e.g. a sign-in page saved as the
  // teacher's material) disappeared exactly when it was needed.
  const fetchOpeningPreview = (notebookId) => {
    setPreviewLoading(true);
    setPreviewFailed(false);
    axios.post(`/api/notebooks/${notebookId}/preview-opening`)
      .then((r) => {
        const p = r.data?.preview || null;
        setOpeningPreview(p);
        if (!p) setPreviewFailed(true);
      })
      .catch(() => { setOpeningPreview(null); setPreviewFailed(true); })
      .finally(() => setPreviewLoading(false));
  };

  // "New link" for an existing notebook: with one class it's instant;
  // with several, an inline picker opens so the choice is explicit
  // (never ambient state the teacher can't see).
  const [linkPickerFor, setLinkPickerFor] = useState(null);
  const [linkPickerSel, setLinkPickerSel] = useState([]);

  const assignNotebook = async (notebookId, explicitIds) => {
    const payload = explicitIds && explicitIds.length
      ? { classroom_invite_ids: explicitIds }
      : assignPayload();
    setBusy(true);
    try {
      const r = await axios.post(`/api/notebooks/${notebookId}/assignments`, payload);
      if (r.data.success) {
        setNewLinks(r.data.assignments || [{ assignment_url: r.data.assignment_url }]);
        setLinkPickerFor(null);
        refresh();
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not create assignment');
    } finally { setBusy(false); }
  };

  const showOutcomes = async (assignmentId) => {
    if (outcomesFor === assignmentId) { setOutcomesFor(null); return; }
    try {
      const r = await axios.get(`/api/notebooks/assignments/${assignmentId}/outcomes`);
      setOutcomes(r.data.outcomes || []);
      setOutcomesFor(assignmentId);
    } catch (e) { flash('Could not load outcomes'); }
  };

  return (
    <section className="teacher-lite-section">
      <h2 className="teacher-lite-subheading">Class materials &amp; assignments</h2>
      <p className="teacher-lite-subhead">
        Paste your materials — links, videos, notes from Gemini Notebook (formerly NotebookLM),
        anything — and get a link your students can start learning from in one click.
      </p>

      {notice && <div className="teacher-step-hint">{notice}</div>}

      {/* THE one-action importer */}
      <div className="teacher-invite-panel">
        <input
          className="teacher-input"
          style={{ marginBottom: 8 }}
          placeholder="Assignment name — students see this (e.g. Unit 1 Review)"
          value={title}
          maxLength={300}
          onChange={(e) => setTitle(e.target.value)}
        />
        {/* Assign-to picker lives HERE, right under the name — the teacher's
            stated mental model: name it → pick the periods → create.
            Always visible so the concept exists even with one class. */}
        {classes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>Assign to:</span>
            {classes.length > 1 && (targetClasses || []).length === 0 && (
              <span style={{ fontSize: 12, color: '#B45309', fontWeight: 600 }}>
                pick the periods →
              </span>
            )}
            {classes.map((c) => {
              const on = (targetClasses || []).includes(c.invite_id);
              const only = classes.length === 1;
              return (
                <button key={c.invite_id} onClick={() => !only && toggleClass(c.invite_id)}
                        disabled={only}
                        title={only ? 'Your only class — add another period to assign selectively' : (on ? 'Click to exclude this class' : 'Click to include this class')}
                        style={{
                          fontSize: 12, padding: '5px 12px', borderRadius: 12,
                          cursor: only ? 'default' : 'pointer',
                          border: `1px solid ${on ? '#297A92' : '#c8d4d8'}`,
                          background: on ? '#297A92' : '#fff',
                          color: on ? '#fff' : '#4a5b60', fontWeight: 600,
                        }}>
                  {on ? '✓ ' : ''}{c.class_name}
                </button>
              );
            })}
            {classes.length > 1 && (
              <button
                className="teacher-btn-ghost"
                style={{ fontSize: 12, padding: '4px 8px' }}
                onClick={() => setTargetClasses(
                  (targetClasses || []).length === classes.length ? [] : classes.map((c) => c.invite_id)
                )}
              >
                {(targetClasses || []).length === classes.length ? 'None' : 'All'}
              </button>
            )}
          </div>
        )}
        <textarea
          className="teacher-input"
          rows={5}
          placeholder={'Paste everything here, any mix:\n• YouTube links (we pull the transcript)\n• Article / web page links\n• Your notebook summary or unit notes'}
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            multiple
            style={{ fontSize: 13 }}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          <button className="teacher-btn-primary" onClick={importAndAssign} disabled={busy}
                  style={{ marginLeft: 'auto' }}>
            {busy ? 'Importing…' : 'Import & get student link →'}
          </button>
        </div>
        {/* Instant grounded preview (Part 16 item 5): proof of grounding
            at import time — a generic opener here means a bad import. */}
        {previewFailed && !previewLoading && (
          <div style={{
            marginTop: 12, padding: '12px 16px', borderRadius: 12,
            background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.35)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              ⚠️ Check before sharing
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
              We couldn't preview what Mira will say about these materials. Before
              you share the link, open it with <strong>Preview as student</strong> and
              make sure she's talking about your actual content.
            </div>
          </div>
        )}

        {(previewLoading || openingPreview) && (
          <div style={{
            marginTop: 12, padding: '12px 16px', borderRadius: 12,
            background: 'rgba(41,122,146,0.07)', border: '1px solid rgba(41,122,146,0.2)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#297A92', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              🎧 What your students will hear first
            </div>
            {previewLoading ? (
              <div style={{ fontSize: 13, color: '#76909f' }}>Mira is reading your materials…</div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontStyle: 'italic', lineHeight: 1.5 }}>
                  “{openingPreview}”
                </div>
                <div style={{ fontSize: 12, color: '#76909f', marginTop: 6 }}>
                  Doesn't sound like your class? Delete the notebook and re-paste your
                  materials — this preview comes from exactly what was imported.
                </div>
              </>
            )}
          </div>
        )}

        {/* Per-class share cards (Part 16 item 4): the teacher thinks
            "share to Period 3", never "which of these URLs is which". */}
        {newLinks.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Ready to share — one link per class:
            </div>
            <div style={{ fontSize: 12.5, color: '#5a6b70', marginBottom: 8 }}>
              This is the only link students need. Post it, paste it, or show the
              QR code — it opens this assignment and adds them to your class.
            </div>
            {newLinks.map((l, i) => (
              <ShareCard key={l.assignment_url || i} link={l} defaultTitle={title} />
            ))}
          </div>
        )}
      </div>

      <p className="teacher-lite-empty-hint" style={{ marginTop: 10 }}>
        Power move: in Gemini Notebook, select your notebook summary, then click the{' '}
        {/* eslint-disable-next-line no-script-url */}
        <a href={BOOKMARKLET_HREF} onClick={(e) => e.preventDefault()}
           style={{ fontWeight: 600, color: '#297A92' }}>
          📔 Push to Tappy
        </a>{' '}
        bookmarklet (drag it to your bookmarks bar once) — same result, zero pasting.
      </p>

      {/* Existing notebooks — re-assign or extend */}
      {notebooks.length > 0 && (
        <>
          <h3 className="teacher-lite-subheading" style={{ marginTop: 20 }}>Your notebooks</h3>
          {notebooks.map((nb) => (
            <div key={nb.id} className="teacher-lite-student-row" style={{ flexWrap: 'wrap' }}>
              <div className="teacher-lite-student-name">
                {nb.title}
                <span className="teacher-lite-student-stats" style={{ marginLeft: 8 }}>
                  {nb.source_count} source{nb.source_count === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="teacher-btn-secondary"
                        disabled={busy || nb.source_count === 0}
                        onClick={() => {
                          if (classes.length <= 1) { assignNotebook(nb.id); return; }
                          setLinkPickerFor(linkPickerFor === nb.id ? null : nb.id);
                          setLinkPickerSel([]); // deliberate choice, same as the importer
                        }}>
                  New link
                </button>
                <button className="teacher-btn-ghost" title="Delete notebook"
                        onClick={async () => {
                          if (!window.confirm(`Delete "${nb.title}"? Its assignments stop working.`)) return;
                          try {
                            await axios.delete(`/api/notebooks/${nb.id}`);
                            flash('Notebook deleted.');
                            refresh();
                          } catch (e) { flash('Could not delete notebook'); }
                        }}>
                  Delete
                </button>
              </div>
              {linkPickerFor === nb.id && (
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>Assign to:</span>
                  {classes.map((c) => {
                    const on = linkPickerSel.includes(c.invite_id);
                    return (
                      <button key={c.invite_id}
                              onClick={() => setLinkPickerSel((prev) =>
                                on ? prev.filter((i) => i !== c.invite_id) : [...prev, c.invite_id])}
                              style={{
                                fontSize: 12, padding: '5px 12px', borderRadius: 12, cursor: 'pointer',
                                border: `1px solid ${on ? '#297A92' : '#c8d4d8'}`,
                                background: on ? '#297A92' : '#fff',
                                color: on ? '#fff' : '#4a5b60', fontWeight: 600,
                              }}>
                        {on ? '✓ ' : ''}{c.class_name}
                      </button>
                    );
                  })}
                  <button className="teacher-btn-primary" style={{ fontSize: 12, padding: '6px 12px' }}
                          disabled={busy || linkPickerSel.length === 0}
                          onClick={() => assignNotebook(nb.id, linkPickerSel)}>
                    Create link{linkPickerSel.length === 1 ? '' : 's'} →
                  </button>
                  <button className="teacher-btn-ghost" style={{ fontSize: 12 }}
                          onClick={() => setLinkPickerFor(null)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Assignments + outcomes */}
      {assignments.length > 0 && (
        <>
          <h3 className="teacher-lite-subheading" style={{ marginTop: 20 }}>Assignments</h3>
          {assignments.map((a) => (
            <div key={a.id} className="teacher-lite-student-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="teacher-lite-student-name" style={{ lineHeight: 1.5 }}>
                {a.title || a.notebook_title}
                <div className="teacher-lite-student-stats" style={{ fontWeight: 400 }}>
                  {a.class_name ? `Assigned to ${a.class_name}` : 'Not linked to a class yet'}
                  {a.created_at && ` on ${new Date(a.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                  {' · '}
                  {a.class_size > 0
                    ? `${a.students_completed} of ${a.class_size} students completed`
                    : `${a.students_completed} student${a.students_completed === 1 ? '' : 's'} completed`}
                  {a.avg_mastery != null && ` · avg mastery ${(a.avg_mastery * 100).toFixed(0)}%`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="teacher-btn-secondary"
                        onClick={async () => {
                          const url = `${window.location.origin}/assignment/${a.share_token}`;
                          try { await navigator.clipboard.writeText(url); flash('Link copied.'); } catch (e) { setNewLinks([{ assignment_url: url, class_name: a.class_name }]); }
                        }}>
                  Copy link
                </button>
                <button className="teacher-btn-secondary"
                        onClick={() => openClassroomShare(
                          `${window.location.origin}/assignment/${a.share_token}`,
                          a.title || a.notebook_title
                        )}>
                  Add to Classroom
                </button>
                <button className="teacher-btn-secondary" title="Open the exact session your students get \u2014 Mira grounded in these materials"
                        onClick={() => window.open(`${window.location.origin}/assignment/${a.share_token}`, '_blank', 'noopener')}>
                  Preview as student
                </button>
                <button className="teacher-btn-secondary" onClick={() => showOutcomes(a.id)}>
                  {outcomesFor === a.id ? 'Hide outcomes' : 'Outcomes'}
                </button>
                <button className="teacher-btn-ghost" title="Delete assignment (the student link stops working; outcomes are kept)"
                        onClick={async () => {
                          if (!window.confirm(`Delete "${a.title || a.notebook_title}"${a.class_name ? ` for ${a.class_name}` : ''}? The student link stops working.`)) return;
                          try {
                            await axios.delete(`/api/notebooks/assignments/${a.id}`);
                            flash('Assignment deleted.');
                            refresh();
                          } catch (e) { flash('Could not delete assignment'); }
                        }}>
                  Delete
                </button>
              </div>
              {outcomesFor === a.id && (
                <div style={{ width: '100%', marginTop: 8 }}>
                  {outcomes.length === 0 ? (
                    <p className="teacher-lite-empty-hint">No sessions yet — share the link with your class.</p>
                  ) : (
                    outcomes.map((o, i) => (
                      <div key={i} className="teacher-lite-preview-row">
                        <span>{o.student_name}</span>
                        <span className="teacher-lite-student-stats">
                          {o.mastery != null ? `mastery ${(o.mastery * 100).toFixed(0)}%` : 'in progress'}
                          {o.questions_count != null && ` · ${o.questions_count} questions`}
                          {o.duration_seconds != null && ` · ${Math.round(o.duration_seconds / 60)} min`}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
};

export default NotebookPanel;
